// agentQuestionsKB.js — Base de connaissance des questions contextuelles
// Système Expert : les questions sont déclenchées par les solutions retenues + conditions projet.
// Format règle : { solution|solutions, typeOuvrage?, condition, question, priorite (1=urgent → 4=général) }
// condition et question acceptent soit un objet/string (format historique) soit une fonction (p, ctx).
import { normaliserZone } from "../utils/normalisation";

// KB-UTIL-01 — Pire cas sol multi-couches (miroir de kb_utils.py)
const DEFAVORABILITE_SOL = {
  roche:1, calcaire_sain:1, calcaire_altere:2, gravier:2,
  sable_dense:3, sable_moyen:3, sable:4, sable_lache:4,
  limon:5, marne:5, argile_raide:5, argile_ferme:6, remblai:6, argile:6,
  argile_molle:7, vase:8, tourbe:8,
};
// Normalise espaces, tirets, accents de casse → clé DEFAVORABILITE_SOL
const _normSol = s => (s || "").toLowerCase().replace(/[\s-]+/g, "_").trim();
// KB-UTIL-01 — Toutes les clés d'une couche sont testées, le score max est retenu
export const getSolCritique = (couches = [], typeSolGlobal = "") => {
  let solCritique = _normSol(typeSolGlobal);
  let scoreMax = DEFAVORABILITE_SOL[solCritique] || 0;
  for (const c of couches) {
    // Tester chaque clé possible : garder la plus défavorable par couche
    for (const candidat of [c.nature, c.typeSol, c.type_sol, c.sol, c.type]) {
      if (!candidat) continue;
      const cs = _normSol(candidat);
      const score = DEFAVORABILITE_SOL[cs] || 0;
      if (score > scoreMax) { scoreMax = score; solCritique = cs; }
    }
  }
  return solCritique;
};

export const QUESTIONS_KB = {

  // ── PIEUX SÉCANTS ──────────────────────────────────────────────────────────

  "Q-PS-01": {
    solution: "pieux_secants",
    condition: { batimentVoisinDistance: { max: 5 } },
    question: "Paroi de pieux sécants H={hauteur}m à {batimentVoisinDistance}m du bâtiment voisin — comment justifier l'ISS (interaction sol-structure) et limiter δH ≤ H/500 (NF P94-282) ?",
    priorite: 1,
  },

  "Q-PS-02": {
    solution: "pieux_secants",
    condition: { nappe: "haute" },
    question: "Nappe phréatique à {niveauNappe}m avec paroi de pieux sécants H={hauteur}m — étanchéité, sous-pressions et gestion sans pompage permanent (NF P94-262) ?",
    priorite: 2,
  },

  "Q-PS-03": {
    solution: "pieux_secants",
    condition: null,
    question: "Capacité portante axiale + effort de soutènement des pieux sécants H={hauteur}m sur {typeSol} — méthode DA2* NF P94-262, quel γRv appliquer ?",
    priorite: 3,
  },

  "Q-PS-04": {
    solution: "pieux_secants",
    condition: { zoneSismique: ["IV", "V", "VI"] },
    question: "Zone sismique {zoneSismique} élevée — tenue des pieux sécants H={hauteur}m : kh Mononobe-Okabe, efforts latéraux et vérification NF EN 1998-5 §5.4 ?",
    priorite: 2,
  },

  // ── PAROI MOULÉE ───────────────────────────────────────────────────────────

  "Q-PM-01": {
    solution: "paroi_moldee",
    condition: { batimentVoisinDistance: { max: 8 } },
    question: "Paroi moulée H={hauteur}m à {batimentVoisinDistance}m du bâtiment voisin — tassements induits méthode Peck et δH admissible (NF P94-282) ?",
    priorite: 1,
  },

  "Q-PM-02": {
    solution: "paroi_moldee",
    condition: { nappe: "haute" },
    question: "Nappe à {niveauNappe}m avec paroi moulée H={hauteur}m — encastrement optimal, radier de fond de fouille et tirants précontraints (NF P94-281 §6) ?",
    priorite: 2,
  },

  "Q-PM-03": {
    solution: "paroi_moldee",
    condition: null,
    question: "Ferraillage paroi moulée H={hauteur}m sur {typeSol} en zone {zoneSismique} — moment de flexion max, armatures verticales et horizontales (NF EN 1992) ?",
    priorite: 3,
  },

  // ── MUR CANTILEVER ─────────────────────────────────────────────────────────

  "Q-MC-01": {
    solution: "mur_cantilever",
    condition: null,
    question: "Mur cantilever BA H={hauteur}m sur {typeSol} (φ={phi}°) — semelle B={B_min}–{B_max}m, SF glissement et renversement en zone {zoneSismique} (NF P94-281) ?",
    priorite: 1,
  },

  "Q-MC-02": {
    solution: "mur_cantilever",
    condition: { zoneSismique: ["III", "IV", "V", "VI"] },
    question: "Vérification sismique mur cantilever H={hauteur}m en zone {zoneSismique} — bêche nécessaire ? kh Mononobe-Okabe et SF glissement minimum (EC8, NF P94-281) ?",
    priorite: 2,
  },

  "Q-MC-03": {
    solution: "mur_cantilever",
    condition: { surcharge: { min: 20 } },
    question: "Mur cantilever H={hauteur}m avec surcharge {surcharge} kN/m² — impact sur la poussée des terres et dimensionnement semelle (EC7 DA2*) ?",
    priorite: 3,
  },

  // ── ÉCRAN ANCRÉ ────────────────────────────────────────────────────────────

  "Q-EA-01": {
    solution: "ecran_ancre",
    condition: { nappe: "haute" },
    question: "Écran ancré H={hauteur}m avec nappe à {niveauNappe}m — profondeur d'encastrement, vérification buée hydraulique et drainage (NF P94-281) ?",
    priorite: 1,
  },

  "Q-EA-02": {
    solution: "ecran_ancre",
    condition: null,
    question: "Tirants d'ancrage pour écran H={hauteur}m sur {typeSol} — longueur libre, longueur scellée et précontrainte optimale (EC7 DA2*, NF P94-153) ?",
    priorite: 2,
  },

  // ── QUESTIONS GÉNÉRALES ────────────────────────────────────────────────────

  "Q-GEN-01": {
    solution: null,
    typeOuvrage: ["soutenement_remblai", "soutenement_deblai"],
    condition: { nappe: "haute" },
    question: "Nappe phréatique à {niveauNappe}m — impact sur l'ouvrage H={hauteur}m : sous-pressions, réduction frottement et drainage à prévoir (EC7 DA2*) ?",
    priorite: 3,
  },

  "Q-GEN-02": {
    solution: null,
    typeOuvrage: ["soutenement_remblai", "soutenement_deblai", "talus_stabilisation", "infrastructure_lineaire"],
    condition: { zoneSismique: ["IV", "V", "VI"] },
    question: "Zone sismique {zoneSismique} élevée — vérifications EC8 supplémentaires pour H={hauteur}m sur {typeSol} (NF EN 1998-5, DA2*) ?",
    priorite: 3,
  },

  // ── FONDATION BÂTIMENT — MULTI-COUCHES Q-FOND-MC ──────────────────────────

  "Q-FOND-MC-01": {
    typeOuvrage: ["fondation_batiment"],
    solutions: ["all"],
    condition: (p, ctx) => {
      // Recalcul depuis couches brutes — ne pas se fier à solCritique en cache
      const sc = getSolCritique(p.couches || ctx.couches || [], p.typeSol || "");
      return ["argile_molle", "limon", "argile_ferme"].includes(sc);
    },
    question: (p, ctx) => {
      const sc = getSolCritique(p.couches || ctx.couches || [], p.typeSol || "");
      return `Pieux béton dans sol argileux (sol critique : ${sc.replace(/_/g, " ")})` +
        ` H=${p.hauteur || "?"}m — encastrement minimal dans couche portante et` +
        ` frottement latéral NF P94-262 ?`;
    },
    priorite: 1,
    theme: "multi-couches",
  },

  "Q-FOND-MC-02": {
    typeOuvrage: ["fondation_batiment"],
    solutions: ["all"],
    condition: (p, ctx) => {
      const sc = getSolCritique(p.couches || ctx.couches || [], p.typeSol || "");
      return ["argile_molle", "limon"].includes(sc);
    },
    question: (p, ctx) => {
      const sc = getSolCritique(p.couches || ctx.couches || [], p.typeSol || "");
      return `Tassement consolidation ${sc.replace(/_/g, " ")} —` +
        ` calcul œdométrique ISO 17892-5 et délai de consolidation admissible ?`;
    },
    priorite: 2,
    theme: "tassements",
  },

  "Q-FOND-MC-03": {
    typeOuvrage: ["fondation_batiment"],
    solutions: ["all"],
    condition: (p, ctx) =>
      ["3", "4", "5", "III", "IV", "V"].includes(String(p.zoneSismique || "")),
    question: (p, ctx) =>
      `Groupe de pieux Zone sismique ${p.zoneSismique} — vérification` +
      ` EC8 NF EN 1998-5 et armatures minimales selon RPS ?`,
    priorite: 2,
    theme: "sismique",
  },

  "Q-FOND-MC-04": {
    typeOuvrage: ["fondation_batiment"],
    solutions: ["all"],
    condition: (p, ctx) =>
      (p.nappe || ctx.nappe) && (p.nappe || ctx.nappe) !== "absente",
    question: (p, ctx) => {
      const nappe = p.nappe || ctx.nappe || "";
      const prof  = p.nappeProf || p.niveauNappe || ctx.nappeProf || ctx.niveauNappe || "?";
      return `Nappe ${nappe} à ${prof}m sous fondation —` +
        ` sous-pressions, drainage et imperméabilisation EC7 §6.7 DA2* ?`;
    },
    priorite: 2,
    theme: "nappe",
  },

  "Q-FOND-MC-05": {
    typeOuvrage: ["fondation_batiment"],
    solutions: ["all"],
    condition: (p, ctx) => {
      const couches = p.couches || ctx.couches || [];
      return couches.length > 1;
    },
    question: (p, ctx) => {
      const couches = p.couches || ctx.couches || [];
      const nc = couches.length;
      const sc = getSolCritique(couches, p.typeSol || "");
      const scLabel = sc.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
      return `Stratigraphie ${nc} couches — sol critique détecté : ${scLabel}.` +
        ` Quelle profondeur d'ancrage optimale selon NF P94-262 ?`;
    },
    priorite: 3,
    theme: "multi-couches",
  },
};

// ─── evaluerCondition ────────────────────────────────────────────────────────
// Retourne true si la condition est satisfaite par les données projet.
// condition = null → toujours vrai
// condition = fonction(p, ctx) → appel direct

export const evaluerCondition = (condition, projet, ctx) => {
  if (!condition) return true;

  // Format fonctionnel (nouvelles règles Q-FOND-MC)
  if (typeof condition === "function") {
    try { return !!condition(projet, ctx || projet); }
    catch { return false; }
  }

  for (const [cle, valeur] of Object.entries(condition)) {
    switch (cle) {

      case "nappe": {
        const n = projet.nappe;
        const nappeActive = n === true || n === "oui" || n === "haute" || n === "moderee" || n === "saisonniere";
        if (!nappeActive) return false;
        break;
      }

      case "zoneSismique": {
        if (!Array.isArray(valeur)) return false;
        if (!valeur.includes(normaliserZone(projet.zoneSismique))) return false;
        break;
      }

      case "batimentVoisinDistance": {
        const dist = parseFloat(projet.batimentVoisinDistance || projet.batiment_voisin_distance);
        if (isNaN(dist)) return false;
        if (valeur.max !== undefined && dist > valeur.max) return false;
        if (valeur.min !== undefined && dist < valeur.min) return false;
        break;
      }

      case "surcharge": {
        const s = parseFloat(projet.surcharge);
        if (isNaN(s)) return false;
        if (valeur.min !== undefined && s < valeur.min) return false;
        if (valeur.max !== undefined && s > valeur.max) return false;
        break;
      }

      default:
        break;
    }
  }

  return true;
};

// ─── interpoler ─────────────────────────────────────────────────────────────
// Remplace les {variables} dans le texte de la question.

export const interpoler = (texte, projet) => {
  const H = parseFloat(projet.hauteur) || 0;
  const phi = parseFloat(projet.frottement) || 20;
  const B_min = (H * 0.5).toFixed(1);
  const B_max = (H * 0.7).toFixed(1);
  const niveauNappe = projet.nappeProf ?? projet.niveauNappe ?? "?";

  return texte
    .replace(/\{hauteur\}/g,               projet.hauteur         || "?")
    .replace(/\{longueur\}/g,              projet.longueur        || "?")
    .replace(/\{zoneSismique\}/g,          projet.zoneSismique    || "?")
    .replace(/\{niveauNappe\}/g,           niveauNappe)
    .replace(/\{surcharge\}/g,             projet.surcharge       || "0")
    .replace(/\{phi\}/g,                   phi)
    .replace(/\{batimentVoisinDistance\}/g, projet.batimentVoisinDistance ?? projet.batiment_voisin_distance ?? "?")
    .replace(/\{typeSol\}/g,               projet.typeSol         || "sol")
    .replace(/\{B_min\}/g,                 B_min)
    .replace(/\{B_max\}/g,                 B_max);
};

// ─── genererQuestions ────────────────────────────────────────────────────────
// Génère max 4 questions contextuelles triées par priorité.
// solutions = tableau [{id, nom, score, ...}] issu du moteur expert (donneesProjet.solutions)

export const genererQuestions = (donneesProjet, solutions) => {
  if (!donneesProjet) return [];

  const dp = donneesProjet;
  const solIds = new Set((solutions || dp.solutions || []).map(s => s.id));

  // KB-UTIL-01 — solCritique : priorité à la valeur pré-calculée par l'appelant
  const ctx = {
    ...dp,
    solCritique: dp.solCritique || getSolCritique(dp.couches || [], dp.typeSol || ""),
  };

  const candidates = [];

  for (const [id, regle] of Object.entries(QUESTIONS_KB)) {

    // Filtre typeOuvrage (règles Q-FOND-MC et futures règles typées)
    if (regle.typeOuvrage && !regle.typeOuvrage.includes(dp.typeOuvrage)) continue;

    // Filtre solution(s) concernée(s)
    if (regle.solutions) {
      // Format tableau : ["all"] → toujours actif, sinon intersection avec solIds
      if (!regle.solutions.includes("all") && !regle.solutions.some(s => solIds.has(s))) continue;
    } else if (regle.solution !== null && regle.solution !== undefined) {
      // Format historique : solution scalaire
      if (!solIds.has(regle.solution)) continue;
    }

    // Filtre condition projet
    if (!evaluerCondition(regle.condition, dp, ctx)) continue;

    // Génération du texte de la question
    let texte;
    if (typeof regle.question === "function") {
      try { texte = regle.question(dp, ctx); }
      catch { continue; }
    } else {
      texte = interpoler(regle.question, dp);
    }

    candidates.push({ id, priorite: regle.priorite, question: texte });
  }

  // Tri par priorité croissante (1 = urgent)
  candidates.sort((a, b) => a.priorite - b.priorite);

  // Max 4 questions, textes uniquement
  return candidates.slice(0, 4).map(c => c.question);
};
